/**
 * 退款：E-2 人工複核工作清單、退款總覽、B-2 退款政策。
 *
 * **AC-API-1 / AC-DM-1**：退款狀態一律以 `(refund_state, ticket_state)` 雙欄
 * 表達，不提供單一 `status` 欄位。本檔各型別均不得新增該欄位。
 */

import { apiFetch, buildQueryString, type Schemas } from './core';

// ---------- E-2 Manual Review（admin.audit_log 權限；AC-E2-5 / E2-6）----------

/** manual_review 工作項 — 對齊 BE ManualReviewItem。 */
export interface ManualReviewItem {
  refund_id: string;
  order_id: string;
  ticket_id: string | null;
  refund_state: string;
  refund_amount: string;
  refund_method: string;
  payment_provider_ref: string | null;
  invoice_allowance_ref: string | null;
  reason: string | null;
  retry_count: number;
  manual_review_due_at: string | null;
  created_at: string;
  updated_at: string;
  /** 已逾 SLA 之分鐘數（<=0 表未逾時） */
  overdue_minutes: number;
}

export interface ManualReviewListResponse {
  items: ManualReviewItem[];
  total: number;
  page: number;
  limit: number;
}

/** refund_state_log timeline 一筆（5W2H）。 */
export interface ManualReviewStateLogEntry {
  id: string;
  from_state: string | null;
  to_state: string;
  reason: string;
  actor_type: 'system' | 'admin' | 'partner';
  actor_user_id: string | null;
  evidence_hash: string | null;
  occurred_at: string;
}

export interface ManualReviewDetail extends ManualReviewItem {
  state_log: ManualReviewStateLogEntry[];
}

export interface ListManualReviewQuery {
  overdue?: boolean;
  sort?: 'due_at' | 'created_at';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

/** GET /admin/refunds/manual-review — manual_review 工作清單（filter / sort / page）。 */
export function listManualReviews(
  query: ListManualReviewQuery = {},
): Promise<ManualReviewListResponse> {
  const qs = buildQueryString({
    overdue: query.overdue ? 'true' : undefined,
    sort: query.sort,
    order: query.order,
    page: query.page,
    limit: query.limit,
  });
  return apiFetch<ManualReviewListResponse>(`/admin/refunds/manual-review${qs}`, { method: 'GET' });
}

/** GET /admin/refunds/manual-review/:id — 細節 + refund_state_log timeline。 */
export function getManualReview(refundId: string): Promise<ManualReviewDetail> {
  return apiFetch<ManualReviewDetail>(
    `/admin/refunds/manual-review/${encodeURIComponent(refundId)}`,
    { method: 'GET' },
  );
}

/**
 * POST /admin/refunds/manual-review/:id/resolve — 督導決議。
 * decision='complete' → review_complete（核准完成）；'cancel' → review_cancel（取消退票）。
 */
export function resolveManualReview(
  refundId: string,
  decision: 'complete' | 'cancel',
  note?: string,
): Promise<unknown> {
  const body: Record<string, unknown> = { decision };
  if (note !== undefined && note.trim().length > 0) body['note'] = note.trim();
  return apiFetch(`/admin/refunds/manual-review/${encodeURIComponent(refundId)}/resolve`, {
    method: 'POST',
    body,
  });
}

// ---------- 退款總覽（GET /admin/refunds；所有 refund_state）----------

/** 退款總覽一列 — 對齊 BE RefundListItem。 */
export interface RefundOverviewItem {
  id: string;
  order_id: string;
  ticket_id: string | null;
  refund_state: string;
  manual_review: boolean;
  refund_amount: string;
  refund_method: string;
  reason: string | null;
  ticket_state: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export interface RefundOverviewListResponse {
  items: RefundOverviewItem[];
  total: number;
  page: number;
  limit: number;
}

export interface ListRefundsQuery {
  state?: string;
  order_id?: string;
  page?: number;
  limit?: number;
}

/** GET /admin/refunds — 退款總覽（涵蓋所有 refund_state；不限 manual_review）。 */
export function listRefunds(query: ListRefundsQuery = {}): Promise<RefundOverviewListResponse> {
  const qs = buildQueryString({
    state: query.state,
    order_id: query.order_id,
    page: query.page,
    limit: query.limit,
  });
  return apiFetch<RefundOverviewListResponse>(`/admin/refunds${qs}`, { method: 'GET' });
}

// ---------- B-2 Refund Policies（admin 權限；PRD §B-2 / AC-B2-1）----------

/** 階梯規則：departure 前 X 小時以上 → 退 ratio (0..1) + 扣 fixed_fee */
export interface RefundPolicyRule {
  hours_before_departure_gte: number;
  refund_ratio: number;
  fixed_fee: number;
}

export type RefundPolicy = Schemas['RefundPolicyResponseDto'];

export interface CreateRefundPolicyDto {
  name: string;
  description?: string;
  time_window_rules: RefundPolicyRule[];
  active?: boolean;
  valid_from?: string;
  valid_to?: string;
}

export interface UpdateRefundPolicyDto {
  name?: string;
  description?: string;
  time_window_rules?: RefundPolicyRule[];
  active?: boolean;
  valid_from?: string;
  valid_to?: string;
}

export interface ListRefundPoliciesQuery {
  include_inactive?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListRefundPoliciesResult {
  items: RefundPolicy[];
  total: number;
  limit: number;
  offset: number;
}

/** GET /admin/refund-policies/current 回傳 RefundPolicy 或 { active: false }（無生效政策時） */
export type CurrentRefundPolicyResponse = RefundPolicy | { active: false };

export function listRefundPolicies(
  query: ListRefundPoliciesQuery = {},
): Promise<ListRefundPoliciesResult> {
  const qs = buildQueryString({
    include_inactive: query.include_inactive,
    limit: query.limit,
    offset: query.offset,
  });
  return apiFetch<ListRefundPoliciesResult>(`/admin/refund-policies${qs}`, { method: 'GET' });
}

export function getCurrentRefundPolicy(): Promise<CurrentRefundPolicyResponse> {
  return apiFetch<CurrentRefundPolicyResponse>('/admin/refund-policies/current', { method: 'GET' });
}

export function getRefundPolicy(id: string): Promise<RefundPolicy> {
  return apiFetch<RefundPolicy>(`/admin/refund-policies/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

export function createRefundPolicy(dto: CreateRefundPolicyDto): Promise<RefundPolicy> {
  return apiFetch<RefundPolicy>('/admin/refund-policies', { method: 'POST', body: dto });
}

export function updateRefundPolicy(id: string, dto: UpdateRefundPolicyDto): Promise<RefundPolicy> {
  return apiFetch<RefundPolicy>(`/admin/refund-policies/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: dto,
  });
}

/** 軟刪除（active=false）；BE 回 200 + 更新後 row（不是 204）。*/
export function deleteRefundPolicy(id: string): Promise<RefundPolicy> {
  return apiFetch<RefundPolicy>(`/admin/refund-policies/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
